import { useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";

import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { cn, COLORS } from "@stacklet/ui";
import { useDeepCompareMemo } from "use-deep-compare";

import { useStickyState } from "app/hooks";

import { useColumnSetup } from "./hooks";
import { LoadingOverlay as CustomLoadingOverlay } from "./LoadingOverlay";
import { NoRowsOverlay as CustomNoRowsOverlay } from "./NoRowsOverlay";
import { CustomFooterComponent as CustomFooter } from "./XGridCustomFooter";
import { XGridCustomToolbar as CustomToolbar } from "./XGridCustomToolbar";

import type {
  DataGridProProps,
  GridApiPro,
  GridAutosizeOptions,
  GridCallbackDetails,
  GridColDef,
  GridColumnHeaderParams,
  GridColumnResizeParams,
  GridDensity,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import type { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";

const DEFAULT_GRID_AUTOSIZE_OPTIONS = {
  includeHeaders: true,
  includeOutliers: true,
  expand: true,
};

const TEST_RUN = import.meta.env.VITEST_WORKER_ID !== undefined;

// Static Tailwind classes for the DataGrid. CSS custom properties (--xgrid-*)
// are injected via `style` so they can reference runtime colour tokens.
const BASE_GRID_CLASSES = [
  "mt-1 bg-transparent text-[14px]",
  "[&.MuiDataGrid-root]:border-none",
  // v7 rounds the grid's corners in two new places: `overflow: hidden` on the
  // root makes the 4px radius it has always carried actually clip, and the
  // scroller (the first child of .main once the drag-only scroll areas render
  // null) gets a matching top radius. v6 did neither, and a headerless,
  // footerless grid puts its first and last rows against those curves.
  "[&.MuiDataGrid-root]:rounded-none",
  "[&_.MuiDataGrid-main>*:first-child]:!rounded-none",
  "[&_.MuiDataGrid-columnHeaders]:shadow-[inset_0_-1px_0_var(--xgrid-row-border-color)]",
  // MUI clips the header container to 4px top corners, which nicks the corner
  // off a focused header's inset 1px outline.
  "[&_.MuiDataGrid-columnHeaders]:rounded-none",
  "[&_.MuiDataGrid-cellCheckbox]:min-w-12 [&_.MuiDataGrid-cellCheckbox]:max-w-12 [&_.MuiDataGrid-cellCheckbox]:justify-center [&_.MuiDataGrid-cellCheckbox]:px-0",
  "[&_.MuiDataGrid-columnHeaderCheckbox]:min-w-12 [&_.MuiDataGrid-columnHeaderCheckbox]:max-w-12 [&_.MuiDataGrid-columnHeaderCheckbox]:justify-center [&_.MuiDataGrid-columnHeaderCheckbox]:px-0",
  "[&_.MuiDataGrid-columnHeaderCheckbox_.MuiDataGrid-columnHeaderTitleContainer]:w-full [&_.MuiDataGrid-columnHeaderCheckbox_.MuiDataGrid-columnHeaderTitleContainer]:justify-center [&_.MuiDataGrid-columnHeaderCheckbox_.MuiDataGrid-columnHeaderTitleContainer]:px-0",
  "[&_.MuiDataGrid-cellCheckbox_.MuiCheckbox-root]:m-0 [&_.MuiDataGrid-columnHeaderCheckbox_.MuiCheckbox-root]:m-0",
  "[&_.MuiDataGrid-row.even]:!bg-data-grid-row-even [&_.MuiDataGrid-row.odd]:!bg-data-grid-row-odd",
  // v7 dropped `.Mui-hovered`; :hover is the only hook left.
  "[&_.MuiDataGrid-row.even:hover]:!bg-data-grid-row-hover",
  "[&_.MuiDataGrid-row.odd:hover]:!bg-data-grid-row-hover",
  // v7 marks the pinned edge with a hard rule in the row-border token instead
  // of v6's soft shadow. Soften it back down.
  "[&_.MuiDataGrid-columnHeader--pinnedLeft]:!border-r-overlay-medium [&_.MuiDataGrid-cell--pinnedLeft]:!border-r-overlay-medium",
  "[&_.MuiDataGrid-columnHeader--pinnedRight]:!border-l-overlay-medium [&_.MuiDataGrid-cell--pinnedRight]:!border-l-overlay-medium",
  // v7 paints pinned cells with `--DataGrid-pinnedBackground`, hiding the row's
  // stripe and hover colour. Repaint them to match the row.
  "[&_.MuiDataGrid-row.even_.MuiDataGrid-cell--pinnedLeft]:!bg-data-grid-row-even [&_.MuiDataGrid-row.even_.MuiDataGrid-cell--pinnedRight]:!bg-data-grid-row-even",
  "[&_.MuiDataGrid-row.odd_.MuiDataGrid-cell--pinnedLeft]:!bg-data-grid-row-odd [&_.MuiDataGrid-row.odd_.MuiDataGrid-cell--pinnedRight]:!bg-data-grid-row-odd",
  "[&_.MuiDataGrid-row.even:hover_.MuiDataGrid-cell--pinnedLeft]:!bg-data-grid-row-hover [&_.MuiDataGrid-row.even:hover_.MuiDataGrid-cell--pinnedRight]:!bg-data-grid-row-hover",
  "[&_.MuiDataGrid-row.odd:hover_.MuiDataGrid-cell--pinnedLeft]:!bg-data-grid-row-hover [&_.MuiDataGrid-row.odd:hover_.MuiDataGrid-cell--pinnedRight]:!bg-data-grid-row-hover",
  "[&_::-webkit-scrollbar]:h-[7px] [&_::-webkit-scrollbar]:w-[7px] [&_::-webkit-scrollbar]:appearance-none",
  "[&_::-webkit-scrollbar-thumb]:rounded-[4px] [&_::-webkit-scrollbar-thumb]:bg-[rgba(0,0,0,0.5)] [&_::-webkit-scrollbar-thumb]:shadow-[0_0_1px_rgba(255,255,255,0.5)]",
  "[&_.MuiButton-textPrimary]:text-[var(--xgrid-toolbar-button-color)]",
  "[&_.MuiDataGrid-cell:focus]:outline-none [&_.MuiDataGrid-cell:focus-within]:outline-none",
  "[&_.MuiDataGrid-columnHeader]:text-label-medium [&_.MuiDataGrid-columnHeader]:text-text-primary",
  "[&_.XGridTable-clickableRow.MuiDataGrid-row:hover]:cursor-pointer [&_.XGridTable-clickableRow.MuiDataGrid-row:hover]:shadow-[inset_5px_0_#0c5688]",
  "[&_.MuiDataGrid-columnHeaderTitleContainer:hover]:cursor-pointer",
  "[&_.MuiDataGrid-columnHeaderTitle]:font-normal",
  // v7 always draws the resize handle, which reads as a rule between every
  // header. Show it on header hover — but always on touch, where :hover never
  // matches.
  "[&_.MuiDataGrid-columnSeparator]:invisible",
  "[&_.MuiDataGrid-columnHeaders:hover_.MuiDataGrid-columnSeparator]:visible",
  "[@media(hover:none)]:[&_.MuiDataGrid-columnSeparator]:!visible",
  // v7 autosize measures the cell itself (v6 measured its first child), and our
  // renderers fill the cell, so every column measures as its current width.
  // While MUI's transient autosizing class is on, shrink the cell and its
  // content to their natural width (capped, so one long value can't blow up the
  // column).
  "[&.MuiDataGrid-autosizing_.MuiDataGrid-cell]:!w-max",
  "[&.MuiDataGrid-autosizing_.MuiDataGrid-cell]:!min-w-0",
  "[&.MuiDataGrid-autosizing_.MuiDataGrid-cell]:!max-w-[640px]",
  "[&.MuiDataGrid-autosizing_.MuiDataGrid-cell_*]:!w-max",
  "[&.MuiDataGrid-autosizing_.MuiDataGrid-cell_*]:!max-w-[640px]",
  "[&.MuiDataGrid-autosizing_.MuiDataGrid-cell_*]:!flex-none",
].join(" ");

interface CustomProps {
  tableId: string;
  autoHeightMax?: number;
  onColumnHeaderClick?: (params: GridColumnHeaderParams) => void;
  handleExport?: () => void;
  hasToolbar?: boolean;
  customHeight?: number | string;
  exportInProgress?: boolean;
  customInline?: React.ReactNode;
  /** Content rendered in the toolbar's right cluster, just before the Columns button */
  toolbarActions?: React.ReactNode;
  /** Moves the export button to the far right as a compact icon-only button */
  exportTrailing?: boolean;
  enableFilterButton?: boolean;
  /** Hides the density selector button in the toolbar when true */
  hideDensityButton?: boolean;
  /** Density used the first time this table renders (before any user override) */
  defaultDensity?: GridDensity;
  /** External apiRef. When provided, used instead of the internally created one. */
  apiRef?: RefObject<GridApiPro>;
  /**
   * Fields to hold at the width their column definition declares, exempt from
   * autosizing. Autosize measures rendered content and then shares any leftover
   * viewport width across the columns it sized, so a column of fixed-size
   * content — an icon button, a status dot — otherwise ends up a different
   * width per table and per viewport. Same reasoning as the checkbox column,
   * which is exempt already.
   */
  fixedWidthFields?: string[];
}

type Props = DataGridProProps & CustomProps;

export function XGridTable({
  autoHeightMax,
  customHeight,
  disableVirtualization,
  handleExport,
  hasToolbar = true,
  exportInProgress,
  initialState,
  loading,
  onColumnHeaderClick,
  tableId,
  rowCount,
  rows = [],
  customInline,
  toolbarActions,
  exportTrailing,
  enableFilterButton = false,
  hideDensityButton = false,
  defaultDensity = "standard",
  fixedWidthFields,
  autosizeOnMount = !TEST_RUN,
  disableColumnMenu,
  disableColumnPinning,
  disableColumnResize,
  checkboxSelection,
  rowSelectionModel: externalRowSelectionModel,
  onRowSelectionModelChange: externalOnRowSelectionModelChange,
  columns: rawColumns,
  rowHeight = 40,
  slotProps: slotPropsFromParent,
  className: parentDataGridClassName,
  style: parentDataGridStyle,
  getRowClassName,
  apiRef: externalApiRef,
  ...rest
}: Props) {
  const [tableState, setTableState] = useStickyState<GridInitialStatePro>(
    {},
    `table-state-${tableId}`,
  );
  // Density has its own sticky key so `defaultDensity` stays the source of
  // truth; v7's copy in exportState is dropped below. v7 also made `density` a
  // controlled prop: the grid refuses its own change unless the prop follows,
  // so `onDensityChange` is what makes the toolbar's buttons take effect.
  const [storedDensity, setTableDensity] = useStickyState<GridDensity>(
    defaultDensity,
    `table-density-${tableId}`,
  );
  // v6 stored density as an object, which v7 throws on. Ignore anything that
  // isn't a v7 density string.
  const tableDensity = isGridDensity(storedDensity)
    ? storedDensity
    : defaultDensity;
  const [internalRowSelectionModel, setInternalRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const isControlled = externalRowSelectionModel !== undefined;
  const rowSelectionModel = isControlled
    ? externalRowSelectionModel
    : internalRowSelectionModel;

  const handleRowSelectionModelChange = (
    newModel: GridRowSelectionModel,
    details: GridCallbackDetails,
  ) => {
    if (!isControlled) {
      setInternalRowSelectionModel(newModel);
    }
    externalOnRowSelectionModelChange?.(newModel, details);
  };

  const [autosizeOptions, setAutosizeOptions] = useState<GridAutosizeOptions>(
    DEFAULT_GRID_AUTOSIZE_OPTIONS,
  );
  const hasAutosizedRef = useRef(false);

  const internalApiRef = useGridApiRef();
  const apiRef = externalApiRef ?? internalApiRef;

  const maxAutoHeightRows = autoHeightMax || 30;
  const totalRows = rowCount || rows.length;

  const userResizedWidthsRef = useRef<Record<string, number>>({});

  const setupColumns = useColumnSetup(rawColumns, totalRows);
  // Deep-compared so an inline `initialState` literal cannot churn the memo.
  const orderedFields = useDeepCompareMemo(
    () => initialState?.columns?.orderedFields,
    [initialState?.columns?.orderedFields],
  );
  // A new `columns` identity makes DataGridPro rebuild widths and order from
  // the prop alone; `initialState` is only read on mount. Reading the ref
  // inside the memo is intentional: widths only need to be re-applied when
  // the identity changes.
  const columns = useMemo(
    () =>
      sortByOrderedFields(setupColumns, orderedFields).map((column) => {
        const width = userResizedWidthsRef.current[column.field];
        return width === undefined
          ? column
          : { ...column, width, flex: undefined };
      }),
    [setupColumns, orderedFields],
  );
  const autosizeEligibleColumns = useMemo(() => {
    const requested = autosizeOptions.columns?.length
      ? new Set(autosizeOptions.columns)
      : null;
    return columns
      .filter(
        (column) =>
          (!requested || requested.has(column.field)) &&
          isAutosizable(column, fixedWidthFields),
      )
      .map((column) => column.field);
  }, [autosizeOptions.columns, columns, fixedWidthFields]);
  // MUI leaves hidden columns out of the flex pool, so `expand` has to stay on
  // when the only flex column is hidden. Prop wins over persisted state, as in
  // `mergeInitialState`.
  const hasVisibleFlexColumn = useMemo(
    () =>
      columns.some(
        (column) =>
          column.flex &&
          (initialState?.columns?.columnVisibilityModel?.[column.field] ??
            tableState?.columns?.columnVisibilityModel?.[column.field]) !==
            false,
      ),
    [columns, initialState, tableState],
  );
  // Measuring headers throws when a caller replaces the columnHeaders slot, and
  // the throw strands MUI's autosizing class on the root (see the catch below).
  const rendersOwnColumnHeaders = rest.slots?.columnHeaders === undefined;
  // A visible flex column already absorbs the leftover width, so `expand` would
  // hand it out twice and overflow.
  const effectiveAutosizeOptions = useMemo(
    () => ({
      ...autosizeOptions,
      includeHeaders: autosizeOptions.includeHeaders && rendersOwnColumnHeaders,
      expand: autosizeOptions.expand && !hasVisibleFlexColumn,
      columns: autosizeEligibleColumns,
    }),
    [
      autosizeOptions,
      autosizeEligibleColumns,
      hasVisibleFlexColumn,
      rendersOwnColumnHeaders,
    ],
  );

  // Re-arm autosizing when the sizing options change, or when the `columns`
  // identity changes — DataGridPro then rebuilds column state at colDef
  // widths (see the memo above), leaving never-user-resized columns at their
  // defaults until the next autosize pass.
  useEffect(() => {
    hasAutosizedRef.current = false;
  }, [autosizeOptions, columns]);

  // MUI's own `autosizeOnMount` pass runs before the rows arrive, so the real
  // sizing happens here — honouring the same prop, since opting out means the
  // caller wants its declared widths kept.
  useEffect(() => {
    if (
      autosizeOnMount &&
      !loading &&
      rows.length &&
      !TEST_RUN &&
      !hasAutosizedRef.current &&
      effectiveAutosizeOptions.columns.length
    ) {
      // Even with the useEffect, there can be a small race between the
      // rendering and autosizing leading to column sizes that are just
      // slightly off. See https://stackoverflow.com/a/78411672
      setTimeout(() => {
        try {
          apiRef.current?.autosizeColumns(effectiveAutosizeOptions);
        } catch (error) {
          // MUI only removes its autosizing class on success. Left on, it pins
          // every cell to max-content and no column width is honoured.
          apiRef.current?.rootElementRef.current?.classList.remove(
            "MuiDataGrid-autosizing",
          );
          console.error("Unable to autosize table columns", tableId, error);
        }
      }, 0);
      hasAutosizedRef.current = true;
    }
    // rows.length (not rows) — avoids re-autosizing on data-only refreshes
    // with the same row count; hasAutosizedRef resets on autosizeOptions or
    // columns change (effectiveAutosizeOptions re-fires this effect then).
  }, [
    autosizeOnMount,
    loading,
    rows.length,
    apiRef,
    effectiveAutosizeOptions,
    tableId,
  ]);

  const mergedSlotProps = {
    ...slotPropsFromParent,
    footer: {
      totalRows,
      checkboxSelection,
      selectedRowsAmount: Array.isArray(rowSelectionModel)
        ? rowSelectionModel.length
        : rowSelectionModel !== undefined && rowSelectionModel !== null
          ? 1
          : 0,
      ...slotPropsFromParent?.footer,
    },
    panel: {
      placement: "bottom-end" as const,
      ...slotPropsFromParent?.panel,
    },
    toolbar: {
      exportInProgress,
      handleExport,
      customInline,
      toolbarActions,
      exportTrailing,
      enableFilterButton,
      hideDensityButton,
      ...slotPropsFromParent?.toolbar,
    },
  };

  // v7 keeps the virtualisation render context in grid state, so `stateChange`
  // now fires on every scrolled row. Re-storing the state there re-renders the
  // grid mid-scroll, so skip when the export hasn't actually changed.
  const lastExportedStateRef = useRef<string | null>(null);

  function onStateChange() {
    const api = apiRef.current;
    if (!api) {
      return;
    }

    if (api.exportState) {
      const exportedState = api.exportState();
      const serialized = JSON.stringify(exportedState);
      if (serialized !== lastExportedStateRef.current) {
        lastExportedStateRef.current = serialized;
        setTableState(exportedState);
      }
    }
  }

  // Don't autosize specific columns if user has manually set their size
  function onColumnResize(params: GridColumnResizeParams) {
    const resizedField = params.colDef.field;

    userResizedWidthsRef.current[resizedField] = params.width;

    setAutosizeOptions((prev) => {
      const currentEligible =
        prev.columns && prev.columns.length
          ? prev.columns
          : columns.map((col) => col.field);

      const nextEligible = currentEligible.filter(
        (field) => field !== resizedField,
      );

      return {
        ...prev,
        columns: nextEligible,
      };
    });
  }

  // `cn` (tailwind-merge) rather than concatenation so a caller's utility can
  // actually override the matching base one instead of tying on specificity.
  const mergedDataGridClassName = cn(
    BASE_GRID_CLASSES,
    parentDataGridClassName,
  );

  const dataGridStyles = {
    "--xgrid-row-border-color": "transparent",
    "--xgrid-toolbar-button-color": COLORS.orange.base,
    ...parentDataGridStyle,
  } as React.CSSProperties;

  const mergedInitialState = withoutDensity(
    mergeInitialState(
      withoutUnreachableState(tableState ?? {}, {
        // The column menu is the only way to pin here, so closing it takes
        // pinning away too.
        pinning: disableColumnPinning || disableColumnMenu,
        widths: disableColumnResize,
      }),
      initialState,
    ),
  );
  const effectiveInitialState = stripPinnedColumnsInTest(
    checkboxSelection
      ? pinCheckboxColumnToLeft(mergedInitialState)
      : mergedInitialState,
  );

  return (
    <div
      className="flex"
      style={{
        height:
          totalRows < maxAutoHeightRows
            ? "100%"
            : customHeight || `calc(100vh - 180px)`,
        minHeight:
          totalRows < maxAutoHeightRows ? `${totalRows * 10}px` : "30vh",
      }}
    >
      <div className="grid h-full w-full">
        <DataGridPro
          apiRef={apiRef}
          autoHeight={totalRows < maxAutoHeightRows}
          autosizeOnMount={autosizeOnMount}
          autosizeOptions={effectiveAutosizeOptions}
          {...rest}
          checkboxSelection={checkboxSelection}
          className={mergedDataGridClassName}
          columns={columns}
          density={tableDensity}
          disableColumnMenu={disableColumnMenu}
          disableColumnPinning={disableColumnPinning}
          disableColumnResize={disableColumnResize}
          disableVirtualization={TEST_RUN || disableVirtualization}
          getRowClassName={
            getRowClassName ??
            ((params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd")
          }
          initialState={effectiveInitialState}
          loading={loading}
          localeText={{ toolbarColumns: "", toolbarFilters: "" }} // remove labels from filter and column buttons
          onColumnHeaderClick={onColumnHeaderClick}
          onColumnResize={onColumnResize}
          onDensityChange={setTableDensity}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          onStateChange={onStateChange}
          rowHeight={rowHeight}
          rowSelectionModel={rowSelectionModel}
          rows={rows}
          slotProps={mergedSlotProps}
          slots={{
            footer: CustomFooter,
            loadingOverlay: CustomLoadingOverlay,
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: hasToolbar ? CustomToolbar : null,
            ...rest.slots,
          }}
          style={dataGridStyles}
          disableColumnReorder
          hideFooterPagination
          hideFooterRowCount
        />
      </div>
    </div>
  );
}

/**
 * Whether autosize may measure and resize this column. Flex columns are left
 * out: v7's `autosizeColumns` writes `flex: 0`, freezing them at whatever pixel
 * width the viewport gave them.
 */
function isAutosizable(column: GridColDef, fixedWidthFields?: string[]) {
  return (
    column.field !== GRID_CHECKBOX_SELECTION_COL_DEF.field &&
    !fixedWidthFields?.includes(column.field) &&
    !column.flex
  );
}

/** The densities v7 accepts. */
const GRID_DENSITIES: readonly GridDensity[] = [
  "compact",
  "standard",
  "comfortable",
];

function isGridDensity(value: unknown): value is GridDensity {
  return (
    typeof value === "string" && GRID_DENSITIES.includes(value as GridDensity)
  );
}

/**
 * Drops the density the grid exported. v7 reads `initialState.density` ahead of
 * the `density` prop, so a stale persisted value would win.
 */
function withoutDensity(state: GridInitialStatePro): GridInitialStatePro {
  if (!("density" in state)) {
    return state;
  }

  const { density: _density, ...rest } = state;
  return rest;
}

/**
 * Drops persisted state the user can no longer reach. Disabling a control never
 * clears what is already stored, so an old pin or width — possibly from another
 * table sharing this `tableId` — would come back with no way to undo it.
 */
function withoutUnreachableState(
  state: GridInitialStatePro,
  unreachable: { pinning?: boolean; widths?: boolean },
): GridInitialStatePro {
  let restorable = state;

  if (unreachable.widths && restorable.columns?.dimensions) {
    const { dimensions: _dimensions, ...columns } = restorable.columns;
    restorable = { ...restorable, columns };
  }

  if (unreachable.pinning && restorable.pinnedColumns) {
    const { pinnedColumns: _pinnedColumns, ...rest } = restorable;
    restorable = rest;
  }

  return restorable;
}

// Same rule DataGridPro applies to `initialState.columns.orderedFields`:
// listed fields first, in that order, then the rest as defined. Grid-injected
// fields (`__check__`, the detail toggle) are prepended by MUI on every
// rebuild, so callers list them first.
function sortByOrderedFields<T extends { field: string }>(
  columns: readonly T[],
  orderedFields: readonly string[] | undefined,
): readonly T[] {
  if (!orderedFields?.length) {
    return columns;
  }

  const rank = new Map(orderedFields.map((field, index) => [field, index]));
  const unlistedRank = orderedFields.length;

  return [...columns].sort(
    (a, b) =>
      (rank.get(a.field) ?? unlistedRank) - (rank.get(b.field) ?? unlistedRank),
  );
}

// The `columns` slices are merged field by field, and the visibility models
// column by column, so a caller's defaults override only what they name and
// the user's persisted choices survive. The persisted `orderedFields` is
// dropped: reorder is disabled, so order comes from the `columns` prop (and
// the caller's `orderedFields`), never from storage.
function mergeInitialState(
  persisted: GridInitialStatePro,
  fromProps: GridInitialStatePro | undefined,
): GridInitialStatePro {
  const { orderedFields: _orderedFields, ...persistedColumns } =
    persisted.columns ?? {};

  return {
    ...persisted,
    ...fromProps,
    columns: {
      ...persistedColumns,
      ...fromProps?.columns,
      columnVisibilityModel: {
        ...persistedColumns.columnVisibilityModel,
        ...fromProps?.columns?.columnVisibilityModel,
      },
    },
  };
}

// Pinned columns render duplicate row DOM under jsdom (virtualization is off in
// tests), which breaks role-based queries like getAllByRole("row"). Strip
// pinned columns during test runs so pinning stays a visual-only concern.
function stripPinnedColumnsInTest(
  state: GridInitialStatePro,
): GridInitialStatePro {
  if (!TEST_RUN || !state.pinnedColumns) {
    return state;
  }

  const { pinnedColumns: _pinnedColumns, ...rest } = state;
  return rest;
}

function pinCheckboxColumnToLeft(
  state: GridInitialStatePro,
): GridInitialStatePro {
  // Disable auto pinning during test runs to avoid duplicate DOM elements getting by getAllByRole
  // and making the tests messy
  if (TEST_RUN) {
    return state;
  }

  const leftPinnedColumns = state.pinnedColumns?.left || [];

  if (!leftPinnedColumns.includes(GRID_CHECKBOX_SELECTION_COL_DEF.field)) {
    return {
      ...state,
      pinnedColumns: {
        ...state.pinnedColumns,
        left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, ...leftPinnedColumns],
      },
    };
  }

  return state;
}
